import type { useSignupChat } from './use-signup-chat';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from 'helpers/cn';
import markUrl from '@/public/logo/mark-color.svg';
import { ChatSuggestions } from '../chat/ChatSuggestions';
import { ChatThread } from '../chat/ChatThread';
import { AuthPrimaryButton } from '../AuthControls';
import { SignupConfirmation } from './SignupConfirmation';
import { SignupDetailsCard } from './SignupDetailsCard';
import { SignupTypeCards } from './SignupTypeCards';

type SignupChatProps = {
  signup: ReturnType<typeof useSignupChat>;
  onBack: () => void;
};

/**
 * The full screen panel's content: a header, the conversation, and at the end the
 * confirmation screen that replaces it.
 */
export const SignupChat = ({ signup, onBack }: SignupChatProps) => {
  const { chat, stage, step, details, errors, suggestions, leaving } = signup;

  return (
    <>
      {/* Three columns so the mark is centred on the screen whatever is on either side. */}
      <header className="grid grid-cols-[1fr_auto_1fr] items-center">
        <button
          type="button"
          onClick={onBack}
          // Not while the account is being created: closing would hide the outcome.
          disabled={signup.stage === 'sending' || leaving}
          // The label is short because it sat oddly at that length in the corner; the
          // name a screen reader gets keeps the whole meaning.
          aria-label="Back to sign in"
          className="text-dim hover:text-foreground hover:bg-hover -ml-2 flex h-8 items-center gap-1.5 justify-self-start rounded-lg px-2 text-body transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ArrowLeft size={15} strokeWidth={1.5} aria-hidden="true" />
          Sign in
        </button>
        <img src={markUrl} alt="Pertento" className="h-[30px] w-auto" />
        <span className="log-line text-dim justify-self-end">{step ? `Step ${step} of 3` : ''}</span>
      </header>

      {stage === 'done' ? (
        <SignupConfirmation firstName={details.first.trim()} email={details.email.trim()} onBack={onBack} />
      ) : (
        <div
          className={cn(
            'mx-auto flex min-h-0 w-full max-w-[760px] flex-1 flex-col',
            leaving && 'pt-chat-leaving pointer-events-none',
          )}
        >
          <ChatThread thread={chat.thread} typing={chat.typing}>
            {signup.showForm && (
              <SignupDetailsCard
                details={details}
                errors={errors}
                onChange={signup.setField}
                onSubmit={signup.submitDetails}
              />
            )}
            {signup.showTypeCards && <SignupTypeCards onChoose={signup.chooseType} />}
            {/* Only the pitch has questions to offer, so only it reserves the row. */}
            <ChatSuggestions suggestions={suggestions} reserve={signup.canBegin} />
          </ChatThread>

          {signup.canBegin && (
            <div className="mx-auto mt-4 mb-7 flex w-full max-w-[760px] flex-col items-center gap-3">
              <AuthPrimaryButton
                type="button"
                onClick={signup.beginSignup}
                trailingIcon={<ArrowRight size={16} strokeWidth={1.5} />}
              >
                Create your account
              </AuthPrimaryButton>
              <span className="log-line text-dim text-center">
                Free onboarding support · 12-day trial · Cookie-free by design
              </span>
            </div>
          )}
        </div>
      )}
    </>
  );
};
